import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Grid from '@mui/material/Unstable_Grid2';
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LoadingButton from '@mui/lab/LoadingButton';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";

import useModal from '../../hooks/use-modal';

import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, SubmitHandler, useForm, useFieldArray } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import { createProjectStyle } from '../../styles/pages/project/create';
import { fileUploadStyle } from '../../styles/common/file-upload';
import { useMutationCreateProject } from '../../api/project';
import { selectAuthUser } from '../../store/auth/selector';

interface KeywordValue {
  typeValue: string;
  keywordValue: string;
}

interface IFormInput {
  projectName: string;
  projectDescription: string;
  keyword: KeywordValue[];
  startDateValue: Date | null;
  endDateValue: Date | null;
  projectThumbnail: File | null;
}

const schema: yup.ObjectSchema<IFormInput> = yup.object({
  projectName: yup.string().required('This field cannot be empty!'),
  projectDescription: yup.string().required('This field cannot be empty!'),
  keyword: yup.array().of(yup.object({typeValue: yup.string().default("AND"), keywordValue: yup.string().required('This field cannot be empty!')})).required('This field cannot be empty!'),
  startDateValue: yup.date().transform((value, originalValue) => {
    if (originalValue === null) {
      return null;
    }
    if (originalValue instanceof Date) {
      return originalValue.toISOString();
    }
    return value;
  })
  .defined()
  .required('This field cannot be empty!')
  .test(
    'startDateValidation', 
    'Start date must be before end date',
    function (startDateValue) {
      const { endDateValue } = this.parent;
  
      if (!endDateValue) return true;
      return startDateValue < endDateValue;
    }
  ),
  endDateValue: yup.date().transform((value, originalValue) => {
    if (originalValue === null) {
      return null;
    }
    if (originalValue instanceof Date) {
      return originalValue.toISOString();
    }
    return value;
  })
  .defined()
  .required('This field cannot be empty!')
  .min(yup.ref('startDateValue'), 'End date must be after start date'),
  projectThumbnail: yup.mixed<File>().required('This field cannot be empty!')
})

export function CreateProject(){
  const navigate = useNavigate();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const profile = useSelector(selectAuthUser);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<IFormInput>({ 
    defaultValues: {
      projectName: '',
      projectDescription: '',
      keyword: [{ typeValue: 'AND', keywordValue: '' }],
      startDateValue: null,
      endDateValue: null,
      projectThumbnail: null
    },
    resolver: yupResolver(schema),
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keyword', // Replace 'items' with the name of your field array
  });

  const [previewThumbnail, setPreviewThumbnail] = useState<string>('');
  const [loadRunProject, setLoadRunProject] = useState(false);

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setValue('projectThumbnail', acceptedFiles[0], {
        shouldValidate: true,
      });

      setPreviewThumbnail(URL.createObjectURL(acceptedFiles[0]))
    }
  
  }, [])

  const onDropRejected = useCallback(rejectedFiles => {
    showModal({
      title: 'Failed',
      message:
        `File is larger than 20 MB`,
      options: {
        variant: 'failed',
      },
    });
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': []
    },
    maxSize: 20 * 1024 * 1024, // 20MB in bytes
    onDropAccepted,
    onDropRejected,
    multiple: false
  });

  const createProject = useMutationCreateProject({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['project'] });
      }
    },
    onSuccess: async (res) => {
      setLoadRunProject(false);
      showModal({
        title: 'Success',
        message:
          'Run project successfully.',
        options: {
          variant: 'success',
          onOk: () => {
            navigate('/project')
          }
        },
      });
    },
    onError: (error) => {
      setLoadRunProject(false);
      if (error.response?.data.errorMessage) {
        showModal({
          title: 'Error',
          message: error.response.data.errorMessage,
          options: {
            variant: 'failed',
          },
        });
      }
    },
  });

  const runProject: SubmitHandler<IFormInput> = data => {
    setLoadRunProject(true)
    
    const formattedString = data.keyword.map((item, index) => {
      if(index !== 0){
        return `${item.typeValue},${item.keywordValue}`
      }

      return `${item.keywordValue}`
    }).join(',')
    
    const formData = new FormData();
    data.projectThumbnail ? formData.append('ProjectThumbnail', data.projectThumbnail) : formData.append('ProjectThumbnail', new Blob());
    formData.append('ProjectCreatorUserId', profile.userId);
    formData.append('ProjectName', data.projectName);
    formData.append('ProjectDetail', data.projectDescription);
    formData.append('ProjectKeyword', formattedString);
    data.startDateValue ? formData.append('StartDate', dayjs(data.startDateValue).format('YYYY-MM-DD')) : formData.append('StartDate', new Blob());
    data.endDateValue ? formData.append('EndDate', dayjs(data.endDateValue).format('YYYY-MM-DD')) : formData.append('EndDate', new Blob());

    createProject.mutate(formData);
  }
  return (
    <Container maxWidth={false} sx={createProjectStyle.contentContainer}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" onClick={() => navigate('/project')}>
                Project
              </Link>
              <Link
                underline="hover"
                color="text.primary"
                onClick={() => navigate('/create-project')}
                aria-current="page"
              >
                Create New Project
              </Link>
            </Breadcrumbs>
          </Grid>
          <Grid xs={6}>
            <Typography variant="h3" sx={createProjectStyle.contentContainerGridItem1Typography}>New Project</Typography>
          </Grid>
          <Grid xs={6}>
            <Box sx={createProjectStyle.contentContainerGridItem2Box}>
              <Button component={Link} onClick={() => navigate('/project')} variant="contained" color="error" sx={createProjectStyle.contentContainerGridItem2BoxButton}>Cancel</Button>
              <LoadingButton
                variant="contained"
                color="success"
                loading={loadRunProject}
                onClick={handleSubmit(runProject)}
              >
                <span>Run Project</span>
              </LoadingButton>
            </Box>
          </Grid>
          <Grid xs={12}>
            <Paper elevation={3} sx={createProjectStyle.contentContainerGridItem3Paper}>
              <Grid container spacing={2}>
                <Grid xs={2}>
                  <Typography variant="subtitle1" sx={createProjectStyle.contentContainerGridItem3PaperGridItem1Typography}>Project Information</Typography>
                  <Typography variant="subtitle2" color="textSecondary">General information about project</Typography>
                </Grid>
                <Grid xs={10} container spacing={2}>
                  <Grid xs={2}>
                    <Box sx={createProjectStyle.contentContainerGridItem3PaperGridItem2ItemBox}>
                      <Typography>Name of project*</Typography>
                    </Box>
                  </Grid>
                  <Grid xs={10}>
                    <Controller
                      name="projectName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => 
                        <TextField 
                          {...field} 
                          fullWidth
                          label="Name of Project" 
                          variant="outlined" 
                          size="small"
                          error={errors.projectName && true}
                          helperText={errors.projectName?.message}
                        />}
                    />
                  </Grid>
                  <Grid xs={2}>
                    <Typography>Brief description*</Typography>
                  </Grid>
                  <Grid xs={10}>
                    <Controller
                      name="projectDescription"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => 
                      <TextField 
                        {...field} 
                        fullWidth
                        label="Brief description"
                        multiline
                        rows={5}
                        error={errors.projectDescription && true}
                        helperText={errors.projectDescription?.message}
                      />}
                    />
                  </Grid>
                </Grid>
                <Grid xs={12}>
                  <Divider/>
                </Grid>
                <Grid xs={2}>
                  <Typography variant="subtitle1" sx={createProjectStyle.contentContainerGridItem3PaperGridItem3Typography}>Keywords</Typography>
                  <Typography variant="subtitle2" color="textSecondary">Related keywords about project</Typography>
                </Grid>
                <Grid xs={10} container spacing={2}>
                  <Grid xs={2}>
                    <Typography>Type*</Typography>
                  </Grid>
                  <Grid xs={10}>
                    <Typography>Input Keyword*</Typography>
                  </Grid>
                  <Grid xs={12}>
                    {fields.map((item, index) => (
                      <Box key={item.id}>
                        <Grid xs={12} container spacing={2}>
                          {index !== 0 ? 
                            <Grid xs={2}>
                              <Controller
                                name={`keyword.${index}.typeValue`}
                                control={control}
                                rules={{ required: true }}
                                defaultValue="AND"
                                render={({ field }) => 
                                  <TextField
                                    {...field}
                                    select
                                    size="small"
                                    fullWidth
                                    error={errors.keyword && errors.keyword[index]?.typeValue && true}
                                    helperText={errors.keyword && errors.keyword[index]?.typeValue?.message}
                                    >
                                      <MenuItem value="AND">And</MenuItem>
                                      <MenuItem value="OR">Or</MenuItem>
                                  </TextField>
                                }
                              />
                            </Grid>
                          : ''
                          }
                          <Grid xs>
                            <Stack direction="row" spacing={2}>
                              <Controller
                                name={`keyword.${index}.keywordValue`}
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => 
                                <TextField 
                                  {...field}  
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  error={errors.keyword && errors.keyword[index]?.keywordValue && true}
                                  helperText={errors.keyword && errors.keyword[index]?.keywordValue?.message}
                                />
                                }
                              />
                              <IconButton 
                                aria-label="delete"
                                onClick={() => {
                                  if (index === 0) {
                                    return
                                  }

                                  remove(index)
                                }}
                              >
                                  <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Grid>
                  <Grid xs={12}>
                    <Button 
                      variant="dashed"
                      startIcon={<AddIcon />}
                      fullWidth
                      onClick={() => append({ typeValue: "AND", keywordValue: ""})}
                    >
                      Add more keyword
                    </Button>
                  </Grid>
                </Grid>
                <Grid xs={12}>
                  <Divider/>
                </Grid>
                <Grid xs={2}>
                  <Typography variant="subtitle1" sx={createProjectStyle.contentContainerGridItem3PaperGridItem4Typography}>Date</Typography>
                  <Typography variant="subtitle2" color="textSecondary">Project runtime</Typography>
                </Grid>
                <Grid xs={10} container spacing={2}>
                  <Grid xs={2}>
                    <Box sx={createProjectStyle.contentContainerGridItem3PaperGridItem5Item1Box}>
                      <Typography>Start date*</Typography>
                    </Box>
                  </Grid>
                  <Grid xs={10}>
                    <Controller
                      name="startDateValue"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) =>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={1} sx={createProjectStyle.contentContainerGridItem3PaperGridItem5Item2Stack}>
                        <DatePicker
                          {...field}
                          slotProps={{ 
                            textField: {
                              size: "small",
                              error: errors.startDateValue ? true : undefined,
                              helperText: errors.startDateValue?.message
                            },
                            inputAdornment: { position: 'start' }
                          }}
                          format="DD-MM-YYYY"
                          maxDate={watch('endDateValue')}
                          />
                        </Stack>
                      </LocalizationProvider>
                      }
                    />
                  </Grid>
                  <Grid xs={2}>
                    <Box sx={createProjectStyle.contentContainerGridItem3PaperGridItem5Item3Box}>
                      <Typography>End Date*</Typography>
                    </Box>
                  </Grid>
                  <Grid xs={10}>
                    <Controller
                      name="endDateValue"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) =>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={1} sx={createProjectStyle.contentContainerGridItem3PaperGridItem5Item4Stack}>
                          <DatePicker
                            {...field}
                            slotProps={{ 
                              textField: {
                                size: "small",
                                error: errors.endDateValue ? true : undefined,
                                helperText: errors.endDateValue?.message
                              },
                              inputAdornment: { position: 'start' }
                            }}
                            format="DD-MM-YYYY"
                            minDate={watch('startDateValue')}
                          />
                        </Stack>
                      </LocalizationProvider>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid xs={12}>
                  <Divider/>
                </Grid>
                <Grid xs={2}>
                  <Typography variant="subtitle1" sx={createProjectStyle.contentContainerGridItem3PaperGridItem6Typography}>Thumbnail</Typography>
                  <Typography variant="subtitle2" color="textSecondary">Image media for project thumbnail</Typography>
                </Grid>
                <Grid xs={10}>
                  <Controller
                    name="projectThumbnail"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur }, fieldState }) => 
                      <Paper elevation={0} {...getRootProps()} className="dropzone" sx={{ ...fileUploadStyle.contentPaper, border: errors.projectThumbnail ? 'solid' : 'none', borderColor: errors.projectThumbnail ? '#CD1818' : '' }}>
                        <Stack direction="column" spacing={2} sx={fileUploadStyle.contentPaperStack}>
                          <Box
                          sx={fileUploadStyle.contentPaperStackBox}
                          >
                            <FileDownloadOutlinedIcon/>
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" sx={fileUploadStyle.contentPaperStackBox2Typography}>Upload a picture for your project thumbnail JPG, JPEG or PNG</Typography>
                            <Typography variant="subtitle2" color="textSecondary">Drag and drop a file less than 20MB</Typography>
                          </Box>
                          <Button
                            variant="contained" 
                            component="label" 
                            sx={fileUploadStyle.contentPaperStackButton}>
                              <input
                                {...getInputProps({
                                  onChange,
                                  onBlur,
                                })}
                              />
                              Or select file
                          </Button>
                        </Stack>
                      </Paper>
                    }
                  />
                  {previewThumbnail && (
                    <Box sx={fileUploadStyle.thumbsContainer}>
                      <Box sx={fileUploadStyle.thumb}>
                        <Box sx={fileUploadStyle.thumbInner}>
                          <Box component="img"
                            src={previewThumbnail}
                            sx={fileUploadStyle.img}
                            // Revoke data uri after image is loaded
                            onLoad={() => { URL.revokeObjectURL(previewThumbnail) }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {errors.projectThumbnail && (
                    <Typography variant="caption" sx={{ color: '#CD1818' }}>{errors.projectThumbnail.message}</Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
    </Container>
  );
}